import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { DecimalField } from "@/components/DecimalInput";
import { intOpts, parseDecimal } from "@/lib/decimal";
import { toast } from "sonner";
import { PackageOpen, Plus, Trash2, Undo2, Check } from "lucide-react";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";

const EDIT_ROLES = ["employee", "versand", "manager", "admin"];
const ANZAHL = intOpts(1, 10000);

function TeilRow({ t, idx, order, canEdit, save, remove }) {
  const status = t.zurueck ? "beim Kunden zurück" : t.muss_zurueck ? "noch bei uns" : "verbleibt bei uns";
  return (
    <li className="py-2 flex items-center gap-3 flex-wrap" data-testid={`kundenteil-${idx}`}>
      <span className="font-medium text-slate-900">{t.bezeichnung}</span>
      <span className="text-xs text-slate-500 font-mono">{t.anzahl} Stück</span>
      <span className={`text-[11px] px-1.5 py-0.5 rounded border ${t.zurueck ? "bg-emerald-50 text-emerald-800 border-emerald-200" : t.muss_zurueck ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`kundenteil-status-${idx}`}>{status}</span>
      {canEdit && (
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
            <Checkbox checked={!!t.muss_zurueck} onCheckedChange={(v) => save(t, { muss_zurueck: !!v })} data-testid={`kundenteil-muss-zurueck-${idx}`} /> muss zurück
          </label>
          <Button size="sm" variant="ghost" onClick={() => save(t, { zurueck: !t.zurueck })} data-testid={`kundenteil-toggle-${idx}`}>
            {t.zurueck ? <><Undo2 className="w-3.5 h-3.5 mr-1" /> noch bei uns</> : <><Check className="w-3.5 h-3.5 mr-1" /> zurück beim Kunden</>}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => remove(t)} aria-label="Kundenteil löschen" data-testid={`kundenteil-delete-${idx}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}
    </li>
  );
}

export function KundenteileSection({ order, user, onUpdated, collapseKey }) {
  const [f, setF] = useState({ bezeichnung: "", anzahl: "1", muss_zurueck: true });
  const [err, setErr] = useState("");
  const canEdit = EDIT_ROLES.includes(user?.rolle);
  const teile = order.kundenteile || [];
  const call = async (fn) => { try { onUpdated((await fn()).data); return true; } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); return false; } };
  const save = (t, patch) => call(() => api.put(`/auftraege/${order.id}/kundenteile/${t.id}`, { bezeichnung: t.bezeichnung, anzahl: t.anzahl, muss_zurueck: t.muss_zurueck, zurueck: t.zurueck, ...patch }));
  const remove = (t) => call(() => api.delete(`/auftraege/${order.id}/kundenteile/${t.id}`));
  const add = async () => {
    const a = parseDecimal(f.anzahl, ANZAHL);
    if (!f.bezeichnung.trim()) { setErr("Bezeichnung erforderlich."); return; }
    if (a.error) { setErr(`Anzahl: ${a.error}`); return; }
    if (await call(() => api.post(`/auftraege/${order.id}/kundenteile`, { bezeichnung: f.bezeichnung.trim(), anzahl: a.value, muss_zurueck: f.muss_zurueck }))) {
      setF({ bezeichnung: "", anzahl: "1", muss_zurueck: true }); setErr("");
    }
  };
  return (
    <Card className="border-slate-200" data-testid="kundenteile-section">
      <CardContent className="p-5 space-y-2">
        <SectionHead k={collapseKey} label="Kundenteile" className="flex items-center gap-2">
          <h2 className="text-base font-semibold flex items-center gap-2"><PackageOpen className="w-4 h-4" /> Kundenteile</h2>
          <SectionSummary k={collapseKey}>{teile.length === 0 ? "keine erfasst" : `${teile.length} ${teile.length === 1 ? "Eintrag" : "Einträge"}${teile.some((t) => t.muss_zurueck && !t.zurueck) ? " · Rückgabe offen" : ""}`}</SectionSummary>
        </SectionHead>
        <SectionBody k={collapseKey} className="space-y-2">
        {teile.length === 0 ? <p className="text-sm text-slate-500">Keine Kundenteile erfasst.</p> : (
          <ul className="divide-y divide-slate-100">{teile.map((t, i) => <TeilRow key={t.id} t={t} idx={i} order={order} canEdit={canEdit} save={save} remove={remove} />)}</ul>
        )}
        {canEdit && (
          <div className="flex items-end gap-2 flex-wrap pt-2 border-t border-slate-100">
            <div className="flex-1 min-w-[200px]"><Label htmlFor="kt-bez" className="text-xs">Bezeichnung</Label>
              <Input id="kt-bez" className="h-9" placeholder="z. B. Scan-Original" value={f.bezeichnung} onChange={(e) => { setF({ ...f, bezeichnung: e.target.value }); setErr(""); }} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="kundenteil-new-bezeichnung" /></div>
            <div className="w-24"><Label htmlFor="kt-anz" className="text-xs">Anzahl</Label>
              <DecimalField id="kt-anz" integer align="left" className="h-9" value={f.anzahl} onChange={(t) => setF({ ...f, anzahl: t })} onKeyDown={(e) => e.key === "Enter" && add()} testId="kundenteil-new-anzahl" /></div>
            <label className="text-sm text-slate-700 inline-flex items-center gap-2 h-9 cursor-pointer">
              <Checkbox checked={f.muss_zurueck} onCheckedChange={(v) => setF({ ...f, muss_zurueck: !!v })} data-testid="kundenteil-new-muss-zurueck" /> muss zurück
            </label>
            <Button size="sm" className="h-9" onClick={add} data-testid="kundenteil-add"><Plus className="w-4 h-4 mr-1" /> Hinzufügen</Button>
            {err && <p className="basis-full text-xs text-rose-600" role="alert" data-testid="kundenteil-error">{err}</p>}
          </div>
        )}
        </SectionBody>
      </CardContent>
    </Card>
  );
}
